// src/services/ApiService.js
import { authService } from './authService';
import { getUserDetails } from "../utils/helpers";

import {
    connectionErrorMsg,
    Message_400,
    Message_401,
    Message_403,
    Message_404,
    Message_405,
    Message_409,
    Message_422,
    Message_500,
    Message_503
} from './errorMsgs';


class ApiService {
    constructor() {
        this.showToast = null;
    }

    /**
     * Get the authenticated axios instance
     */
    getApi() {
        return authService.getApi();
    }

    /**
     * Set the toast function from useToast hook
     */
    setToastFunction(toastFunction) {
        this.showToast = toastFunction;
    }

    /**
     * Handle API errors consistently based on backend controller structure
     */
    handleError(error, defaultMessage = 'Operation failed') {
        let message = defaultMessage;
        let statusCode = null;
        let errors = null;
        let isIntegrityConstraint = false;
        let isDatabaseError = false;
        let errorData = null;

        if (error.response) {
            statusCode = error.response.status;
            const responseData = error.response.data || {};
            // Store complete error data for reference
            errorData = responseData;
            // Use backend message if available, otherwise fallback to our defaults
            message = responseData.message || defaultMessage;
            errors = responseData.errors || null;

            // Check for database integrity constraint errors
            const backendError = responseData.error || '';
            isIntegrityConstraint = backendError.includes('Integrity constraint violation') ||
                                backendError.includes('foreign key constraint fails') ||
                                backendError.includes('1451') ||
                                backendError.includes('Integrity constraint');
            
            // Check for database errors
            isDatabaseError = backendError.includes('Database Error') || 
                           responseData.message?.includes('Database Error') ||
                           backendError.includes('SQLSTATE') ||
                           backendError.includes('QueryException');

            // Handle specific backend response patterns
            switch (statusCode) {
                case 400:
                    // Handle business logic errors (like "Cannot delete stock item with positive balance")
                    if (responseData.message) {message = responseData.message;}
                    else {message = Message_400;}
                    break;
                case 401:
                    message = message || Message_401;
                    break;
                case 403:
                    message = message || Message_403;
                    break;
                case 404:
                    message = message || Message_404;
                    break;
                case 405:
                    message = message || Message_405;
                    break;
                case 409:
                    // Handle duplicate/conflict errors
                    if (responseData.message && responseData.message.includes('already exists')) {
                        message = responseData.message;
                    } else {
                        message = message || Message_409;
                    }
                    break;
                case 422:
                    // Backend returns validation errors with 'errors' object
                    if (errors) {
                        // Format validation errors into a readable string
                        const errorMessages = Object.values(errors)
                            .flat()
                            .map(err => `• ${err}`)
                            .join('\n');
                        message = `${responseData.message || 'Validation failed'}:\n${errorMessages}`;
                    }
                    else {message = responseData.message || Message_422;}
                    break;
                case 500:
                    // Handle database integrity constraint errors
                    if (isIntegrityConstraint) {
                        message = 'Cannot perform this action because it has related records. ' +
                                'Please delete the related records first.';
                    } 
                    // Handle database errors
                    else if (isDatabaseError) {
                        message = 'A database error occurred. Please try again.';
                        
                        // Extract meaningful error message if available
                        if (backendError) {
                            // Try to extract user-friendly error message
                            const dbErrorMatch = backendError.match(/(?:SQLSTATE\[\d+\]\s*\[.*?\]\s*)?(.*)/);
                            if (dbErrorMatch && dbErrorMatch[1]) {
                                const dbMessage = dbErrorMatch[1];
                                // Clean up the message for user display
                                message = this.extractUserFriendlyErrorMessage(dbMessage);
                            }
                        }
                    }
                    else {message = message || Message_500;}
                    break;
                case 503:
                    message = message || Message_503;
                    break;
                default:
                    message = message || `Server error (${statusCode})`;
            }
        } else if (error.request) {
            message = connectionErrorMsg;
        } else {
            message = error.message || defaultMessage;
        }

        if (this.showToast) {
            this.showToast("Error", message, "error");
        } else {
            console.warn('Toast function not set. Message:', message);
        }

        return {
            success: false,
            message,
            statusCode,
            errors,
            isIntegrityConstraint,
            isDatabaseError,
            error: errorData || error
        };
    }

    /**
     * Extract user-friendly error message from database error
     */
    extractUserFriendlyErrorMessage(dbMessage) {
        // Common database error patterns and their user-friendly equivalents
        const errorPatterns = [
            { pattern: /cannot be null/i, message: 'Required field cannot be empty' },
            { pattern: /duplicate entry/i, message: 'This record already exists' },
            { pattern: /foreign key constraint/i, message: 'Cannot delete this record because it has related records' },
            { pattern: /data too long/i, message: 'Input is too long for the field' },
            { pattern: /invalid date/i, message: 'Invalid date format' },
            { pattern: /incorrect integer value/i, message: 'Invalid number format' },
            { pattern: /unknown column/i, message: 'Unknown column in the table' },
            { pattern: /table.*?doesn't exist/i, message: 'System configuration error' },
            { pattern: /connection refused/i, message: 'Cannot connect to database' },
            { pattern: /access denied/i, message: 'Database access denied' },
            { pattern: /out of range/i, message: 'Value is out of acceptable range' },
            { pattern: /division by zero/i, message: 'Mathematical calculation error' },
            { pattern: /unique constraint/i, message: 'This value must be unique' },
        ];

        // Check for specific patterns
        for (const pattern of errorPatterns) {
            if (pattern.pattern.test(dbMessage)) {
                return pattern.message;
            }
        }

        // Default message for database errors
        return 'A database error occurred. Please check your input and try again.';
    }

    /**
     * Format error messages from backend errors object
     */
    formatErrorMessages(errors) {
        if (!errors) return '';
        return Object.values(errors)
            .flat()
            .map(err => `• ${err}`)
            .join('\n');
    }

    /**
     * Get names list (for dropdowns) - Generic method
     */
    async listNames(API) {
        try {
            const params = {};
            const currentUserId = getUserDetails("Id");
            if (currentUserId) { 
                params.user_id = currentUserId;
            }
            const api = this.getApi();
            const response = await api.get(API, { params });
            const responseData = response.data;

            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data || [],
                    message: responseData.message || 'Data fetched successfully'
                };
            } else {
                // Backend returned error status
                const errorMessage = responseData.message || 'Failed to fetch data';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: responseData.data || null,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch data');
        }
    }

    /**
     * Get list with full details (for tables) - Generic method
     */
    async list(API) {
        try {
            const params = {};
            const currentUserId = getUserDetails("Id");
            if (currentUserId) { 
                params.user_id = currentUserId;
            }
            
            const api = this.getApi();
            const response = await api.get(API, { params });
            const responseData = response.data;

            // Check backend response status
            if (responseData.status === 'success') {
                const data = Array.isArray(responseData.data) ? responseData.data : [];
                return {
                    success: true,
                    data: data,
                    message: responseData.message || 'Records fetched successfully'
                };
            } else {
                // Backend returned error status
                const errorMessage = responseData.message || 'Failed to fetch records';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: responseData.data || null,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch records');
        }
    }
    async listPlatformAdminDashboard(API) {
        try {
            const params = {};
            const currentUserId = getUserDetails("Id");
            if (currentUserId) { 
                params.user_id = currentUserId;
            }
            
            const api = this.getApi();
            const response = await api.get(API, { params });
            const responseData = response.data;

            // Check backend response status
            if (responseData.status === 'success') {
                // Allow both arrays and objects to be returned safely
                const data = responseData.data !== undefined ? responseData.data : null;
                return {
                    success: true,
                    data: data,
                    message: responseData.message || 'Records fetched successfully'
                };
            } else {
                // Backend returned error status
                const errorMessage = responseData.message || 'Failed to fetch records';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: responseData.data || null,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch records');
        }
    }
     /**
     * Get list with inistution
     */
    async listByInstitution(API) {
        try {
            const params = {};
            const currentUserId = getUserDetails("Id");
            const institutionId = getUserDetails("universityId");
            if (currentUserId) {
                params.user_id = currentUserId;
                params.institution_id = institutionId;
            }

            const api = this.getApi();
            const response = await api.get(`${API}`, { params });
            const responseData = response.data;

            // Check backend response status
            if (responseData.status === 'success') {
                const data = Array.isArray(responseData.data) ? responseData.data : [];
                return {
                    success: true,
                    data: data,
                    message: responseData.message || 'Records fetched successfully'
                };
            } else {
                // Backend returned error status
                const errorMessage = responseData.message || 'Failed to fetch records';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: responseData.data || null,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch records');
        }
    }

    /**
     * Get list with inistution Id and user Id 
     */
    async listByInstitutionAndId(API) {
        try {
            const params = {};
            const currentUserId = getUserDetails("Id");
            const institutionId = getUserDetails("universityId");
            if (currentUserId) {
                params.user_id = currentUserId;
                params.institution_id = institutionId;
            }

            const api = this.getApi();
            const response = await api.get(`${API}`, { params });
            const responseData = response.data;

            // Check backend response status
            if (responseData.status === 'success') {
                const data = Array.isArray(responseData.data) ? responseData.data : [];
                return {
                    success: true,
                    data: data,
                    message: responseData.message || 'Records fetched successfully'
                };
            } else {
                // Backend returned error status
                const errorMessage = responseData.message || 'Failed to fetch records';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: responseData.data || null,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch records');
        }
    }
// Add to your apiService or create a new method

// In apiServices/ApiService.js or similar


    /**
     * Get list with full details (for tables) - Generic method with ID parameter
     */
    async listById(API, id) {
        try {
            const params = {};
            const currentUserId = getUserDetails("Id");
            if (currentUserId) { 
                params.user_id = currentUserId;
            }
            
            const api = this.getApi();
            const response = await api.get(`${API}/${id}`, { params });
            const responseData = response.data;

            // Check backend response status
            if (responseData.status === 'success') {
                const data = Array.isArray(responseData.data) ? responseData.data : [];
                return {
                    success: true,
                    data: data,
                    message: responseData.message || 'Records fetched successfully'
                };
            } else {
                // Backend returned error status
                const errorMessage = responseData.message || 'Failed to fetch records';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: responseData.data || null,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch records');
        }
    }


    /**
     * POST request - Generic method for custom endpoints
     * @param {string} API - The API endpoint
     * @param {Object} data - The data to send
     * @param {Object} config - Additional axios config options
     * @returns {Promise<Object>} - Returns the response
     */
    async post(API, data = {}, config = {}) {
        try {
            const params = {};
            const currentUserId = getUserDetails("Id");
            if (currentUserId) {
                params.user_id = currentUserId;
            }

            const api = this.getApi();
            const response = await api.post(API, data, { ...config, params });
            const responseData = response.data;

            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message || 'Operation successful'
                };
            } else {
                // Handle different error cases
                let errorMessage = responseData.message || 'Operation failed';

                // Handle validation errors (422)
                if (response.status === 422 && responseData.errors) {
                    const errorMessages = this.formatErrorMessages(responseData.errors);
                    errorMessage = `${responseData.message || 'Validation failed'}:\n${errorMessages}`;
                }

                // Handle database errors from backend
                if (response.status === 500 && responseData.error) {
                    errorMessage = this.extractUserFriendlyErrorMessage(responseData.error);
                }

                // Handle conflict errors (409)
                if (response.status === 409 && responseData.data) {
                    // Return the full response for duplicate checking
                    return {
                        success: false,
                        message: errorMessage,
                        data: responseData.data,
                        statusCode: response.status
                    };
                }

                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }

                return {
                    success: false,
                    data: responseData.data,
                    message: errorMessage,
                    errors: responseData.errors,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Operation failed');
        }
    }
    async listDashboardData(API, id) {
        try {
            const params = {};
            const currentUserId = getUserDetails("Id");

            if (currentUserId) {
                params.user_id = currentUserId;
            }

            const api = this.getApi();
            const response = await api.get(`${API}/${id}`, { params });

            const responseData = response.data;

            if (responseData.status === 'success') {

                return {
                    success: true,
                    data: responseData.data || null,
                    message: responseData.message || 'Records fetched successfully'
                };

            } else {

                const errorMessage =
                    responseData.message || 'Failed to fetch records';

                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }

                return {
                    success: false,
                    data: responseData.data || null,
                    message: errorMessage,
                    statusCode: response.status
                };
            }

        } catch (error) {
            return this.handleError(error, 'Failed to fetch records');
        }
    }
    /**
     * Create record - Generic method
     */
    async create(API, formData) {
        try {
            const api = this.getApi();
            const response = await api.post(API, formData);
            const responseData = response.data;
            
            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message || 'Record created successfully'
                };
            } else {
                // Handle different error cases
                let errorMessage = responseData.message || 'Failed to create record';
                // Handle validation errors (422)
                if (response.status === 422 && responseData.errors) {
                    const errorMessages = this.formatErrorMessages(responseData.errors);
                    errorMessage = `${responseData.message || 'Validation failed'}:\n${errorMessages}`;
                }
                // Handle database errors from backend
                if (response.status === 500 && responseData.error) {
                    errorMessage = this.extractUserFriendlyErrorMessage(responseData.error);
                }
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: responseData.data,
                    message: errorMessage,
                    errors: responseData.errors,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to create record');
        }
    }

    /**
     * Update record - Generic method
     */
    async update(API, formData) {
        try {
            const params = {};
            const currentUserId = getUserDetails("Id");
            if (currentUserId) { 
                params.user_id = currentUserId;
            }
            
            const api = this.getApi();
            const response = await api.post(API, formData, { params });
            const responseData = response.data;

            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message || 'Record updated successfully'
                };
            } else {
                // Handle different error cases
                let errorMessage = responseData.message || 'Failed to update record';

                // Handle validation errors (422)
                if (response.status === 422 && responseData.errors) {
                    const errorMessages = this.formatErrorMessages(responseData.errors);
                    errorMessage = `${responseData.message || 'Validation failed'}:\n${errorMessages}`;
                }

                // Handle database errors from backend
                if (response.status === 500 && responseData.error) {
                    errorMessage = this.extractUserFriendlyErrorMessage(responseData.error);
                }

                // Handle record not found (404)
                if (response.status === 404) {
                    errorMessage = responseData.message || 'Record not found';
                }

                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: responseData.data,
                    message: errorMessage,
                    errors: responseData.errors,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to update record');
        }
    }

    /**
     * Delete record - Generic method
     */
    async delete(API, deleteData) {
        try {
            const api = this.getApi();
            const response = await api.post(API, deleteData);
            const responseData = response.data;
            
            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true,
                    message: responseData.message || 'Record deleted successfully'
                };
            } else {
                // Handle different error cases
                let errorMessage = responseData.message || 'Failed to delete record';
                // Handle business logic errors (400 status with custom message)
                if (response.status === 400) {
                    if (responseData.message) {
                        errorMessage = responseData.message;
                    } else {
                        errorMessage = Message_400;
                    }
                }
                // Handle record not found (404)
                if (response.status === 404) {
                    errorMessage = responseData.message || 'Record not found';
                }
                // Handle validation errors (422) - for cases where validation fails in delete
                if (response.status === 422 && responseData.errors) {
                    const errorMessages = this.formatErrorMessages(responseData.errors);
                    errorMessage = `${responseData.message || 'Validation failed'}:\n${errorMessages}`;
                }
                // Handle database errors from backend
                if (response.status === 500 && responseData.error) {
                    errorMessage = this.extractUserFriendlyErrorMessage(responseData.error);
                }
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to delete record');
        }
    }

    /**
     * Get single record by ID - Generic method
     */
    async show(API, id) {
        try {
            const params = {};
            const currentUserId = getUserDetails("Id");
            if (currentUserId) { 
                params.user_id = currentUserId;
            }
            
            const api = this.getApi();
            const response = await api.get(`${API}/${id}`, { params });
            const responseData = response.data;

            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message || 'Record fetched successfully'
                };
            } else {
                // Backend returned error status
                const errorMessage = responseData.message || 'Failed to fetch record';

                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }

                return {
                    success: false,
                    data: responseData.data || null,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch record');
        }
    }

    /**
     * Print record - Generate and download PDF
     * This method is specifically for generating print/PDF reports
     */
    async print(API, formData) {
        try {
            const api = this.getApi();
            const response = await api.post(API, formData, {
                responseType: 'blob', // Important for file downloads
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Check if response is blob
            if (response.data instanceof Blob) {
                // Check if it's a PDF blob
                if (response.data.type === 'application/pdf' || 
                    response.headers['content-type'] === 'application/pdf') {
                    
                    // Create blob URL
                    const blobUrl = window.URL.createObjectURL(response.data);
                    
                    // For PDF, open in new tab
                    window.open(blobUrl, '_blank');
                    
                    // Clean up after a delay
                    setTimeout(() => {
                        window.URL.revokeObjectURL(blobUrl);
                    }, 1000);
                    
                    return {
                        success: true,
                        message: 'PDF generated successfully',
                        data: { blobUrl }
                    };
                } else {
                    // For JSON response (error case)
                    const text = await response.data.text();
                    const jsonData = JSON.parse(text);
                    
                    if (jsonData.status === 'success') {
                        // If success but with PDF URL
                        if (jsonData.data && jsonData.data.pdf_url) {
                            window.open(jsonData.data.pdf_url, '_blank');
                            return {
                                success: true,
                                message: jsonData.message || 'PDF generated successfully',
                                data: jsonData.data
                            };
                        }
                        return {
                            success: true,
                            message: jsonData.message || 'Operation successful',
                            data: jsonData.data
                        };
                    } else {
                        // Error from backend
                        return {
                            success: false,
                            message: jsonData.message || 'Failed to generate PDF',
                            errors: jsonData.errors
                        };
                    }
                }
            } else {
                // Handle JSON response (non-blob)
                const responseData = response.data;
                
                if (responseData.status === 'success') {
                    if (responseData.data && responseData.data.pdf_url) {
                        window.open(responseData.data.pdf_url, '_blank');
                    }
                    return {
                        success: true,
                        message: responseData.message || 'PDF generated successfully',
                        data: responseData.data
                    };
                } else {
                    return {
                        success: false,
                        message: responseData.message || 'Failed to generate PDF',
                        errors: responseData.errors
                    };
                }
            }
        } catch (error) {
            // Handle error with special consideration for blob responses
            if (error.response && error.response.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const jsonData = JSON.parse(text);
                    return this.handleError(
                        { ...error, response: { ...error.response, data: jsonData } }, 
                        'Failed to generate PDF'
                    );
                } catch (parseError) {
                    return this.handleError(error, 'Failed to generate PDF');
                }
            }
            return this.handleError(error, 'Failed to generate PDF');
        }
    }

    /**
     * Download file - Generic method for file downloads
     */
    async download(API, formData, filename = 'download.pdf') {
        try {
            const api = this.getApi();
            const response = await api.post(API, formData, {
                responseType: 'blob',
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });

            // Create blob URL
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const blobUrl = window.URL.createObjectURL(blob);

            // Create download link
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up
            window.URL.revokeObjectURL(blobUrl);
            
            return {
                success: true,
                message: 'File downloaded successfully'
            };
        } catch (error) {
            return this.handleError(error, 'Failed to download file');
        }
    }

    /**
     * Export all to Excel
     */
    async exportToExcel(API, formData) {
        try {
            const api = this.getApi();
            const response = await api.post(API, formData, {
                responseType: 'blob',
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });

            if (response.data instanceof Blob) {
                const blobUrl = window.URL.createObjectURL(response.data);
                const filename = `export_${new Date().toISOString().split('T')[0]}.xlsx`;
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                setTimeout(() => {
                    window.URL.revokeObjectURL(blobUrl);
                }, 100);
                
                return {
                    success: true,
                    message: 'Excel file generated successfully'
                };
            } else {
                const responseData = response.data;
                if (responseData.status === 'success') {
                    return {
                        success: true,
                        message: responseData.message || 'Excel file generated successfully'
                    };
                } else {
                    return {
                        success: false,
                        message: responseData.message || 'Failed to generate Excel file'
                    };
                }
            }
        } catch (error) {
            return this.handleError(error, 'Failed to export to Excel');
        }
    }

    /**
     * Export all to PDF
     */
    async exportToPDF(API, formData) {
        try {
            const api = this.getApi();
            const response = await api.post(API, formData, {
                responseType: 'blob',
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });

            if (response.data instanceof Blob) {
                const blobUrl = window.URL.createObjectURL(response.data);
                window.open(blobUrl, '_blank');
                
                setTimeout(() => {
                    window.URL.revokeObjectURL(blobUrl);
                }, 1000);
                
                return {
                    success: true,
                    message: 'PDF file generated successfully'
                };
            } else {
                const responseData = response.data;
                if (responseData.status === 'success') {
                    if (responseData.data && responseData.data.pdf_url) {
                        window.open(responseData.data.pdf_url, '_blank');
                    }
                    return {
                        success: true,
                        message: responseData.message || 'PDF file generated successfully'
                    };
                } else {
                    return {
                        success: false,
                        message: responseData.message || 'Failed to generate PDF file'
                    };
                }
            }
        } catch (error) {
            return this.handleError(error, 'Failed to export to PDF');
        }
    }

    /**
     * Check if error is database related
     */
    isDatabaseError(error) {
        const errorMessage = error.message || '';
        const errorData = error.error || '';

        return errorMessage.includes('Database Error') ||
            errorData.includes('Database Error') ||
            errorMessage.includes('SQLSTATE') ||
            errorData.includes('SQLSTATE') ||
            errorMessage.includes('Integrity constraint') ||
            errorData.includes('Integrity constraint');
    }

    // ====================================== Notifications functions ==================================
        /**
     * Get all notifications with pagination and filters
     * @param {string} API - The API endpoint
     * @param {Object} params - Query parameters (page, per_page, is_read, type, user_type, search, etc.)
     * @returns {Promise<Object>} - Returns paginated notifications with unread count
     */
    async getAllNotifications(API, params = {}) {
        try {
            const currentUserId = getUserDetails("Id");
            if (currentUserId && !params.user_id && !params.student_id && !params.supervisor_id && !params.institution_admin_id) {
                // Don't automatically add user_id - let the specific notification handlers add their own IDs
            }
            
            const api = this.getApi();
            const response = await api.get(API, { params });
            const responseData = response.data;

            // Check backend response structure
            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data || [],
                    pagination: responseData.pagination || null,
                    unread_count: responseData.unread_count || 0,
                    message: responseData.message || 'Notifications fetched successfully'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to fetch notifications';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: [],
                    message: errorMessage,
                    statusCode: response.status,
                    unread_count: 0
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch notifications');
        }
    }

    /**
     * Get recent notifications for header/bell icon display
     * @param {string} API - The API endpoint for recent notifications
     * @param {Object} params - Parameters (limit, user_id, etc.)
     * @returns {Promise<Object>} - Returns recent notifications with unread count
     */
    async getRecentNotifications(API, params = {}) {
        try {
            const api = this.getApi();
            const response = await api.get(API, { params });
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data || [],
                    unread_count: responseData.unread_count || 0,
                    message: responseData.message || 'Recent notifications fetched successfully'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to fetch recent notifications';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: [],
                    message: errorMessage,
                    unread_count: 0,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch recent notifications');
        }
    }

    /**
     * Get notification statistics (counts by type, status, etc.)
     * @param {string} API - The API endpoint for statistics
     * @param {Object} params - Parameters (user_id, student_id, supervisor_id, etc.)
     * @returns {Promise<Object>} - Returns notification statistics
     */
    async getNotificationStatistics(API, params = {}) {
        try {
            const api = this.getApi();
            const response = await api.get(API, { params });
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data || {},
                    message: responseData.message || 'Statistics fetched successfully'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to fetch statistics';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: null,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch notification statistics');
        }
    }

    /**
     * Mark a single notification as read
     * @param {string} API - The API endpoint for marking as read (e.g., /notifications/{id}/read)
     * @param {Object} data - Optional data to send (like user_id for verification)
     * @returns {Promise<Object>} - Returns success status
     */
    async markNotificationAsRead(API, data = {}) {
        try {
            const api = this.getApi();
            const response = await api.put(API, data);
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message || 'Notification marked as read'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to mark notification as read';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to mark notification as read');
        }
    }

    /**
     * Mark all notifications as read for a user
     * @param {string} API - The API endpoint for marking all as read
     * @param {Object} data - Data containing user identifier (user_id, student_id, etc.)
     * @returns {Promise<Object>} - Returns success status
     */
    async markAllNotificationsAsRead(API, data = {}) {
        try {
            const api = this.getApi();
            const response = await api.put(API, data);
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    message: responseData.message || 'All notifications marked as read'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to mark all notifications as read';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to mark all notifications as read');
        }
    }

    /**
     * Delete a single notification
     * @param {string} API - The API endpoint for deletion (e.g., /notifications/{id})
     * @returns {Promise<Object>} - Returns success status
     */
    async deleteNotification(API) {
        try {
            const api = this.getApi();
            const response = await api.delete(API);
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    message: responseData.message || 'Notification deleted successfully'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to delete notification';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to delete notification');
        }
    }

    /**
     * Bulk delete multiple notifications
     * @param {string} API - The API endpoint for bulk deletion
     * @param {Object} data - Data containing array of notification IDs
     * @returns {Promise<Object>} - Returns success status
     */
    async bulkDeleteNotifications(API, data = {}) {
        try {
            const api = this.getApi();
            const response = await api.post(API, data);
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    message: responseData.message || 'Notifications deleted successfully'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to delete notifications';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to delete notifications');
        }
    }

    /**
     * Get a single notification by ID
     * @param {string} API - The API endpoint with ID (e.g., /notifications/{id})
     * @returns {Promise<Object>} - Returns notification details
     */
    async getNotificationById(API) {
        try {
            const api = this.getApi();
            const response = await api.get(API);
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message || 'Notification fetched successfully'
                };
            } else {
                const errorMessage = responseData.message || 'Failed to fetch notification';
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    data: null,
                    message: errorMessage,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch notification');
        }
    }

    /**
     * Create a new notification (for admin/system use)
     * @param {string} API - The API endpoint for creating notifications
     * @param {Object} formData - Notification data (title, message, type, user_type, etc.)
     * @returns {Promise<Object>} - Returns created notification
     */
    async createNotification(API, formData) {
        try {
            const api = this.getApi();
            const response = await api.post(API, formData);
            const responseData = response.data;

            if (responseData.success === true || responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message || 'Notification created successfully'
                };
            } else {
                let errorMessage = responseData.message || 'Failed to create notification';
                if (response.status === 422 && responseData.errors) {
                    const errorMessages = this.formatErrorMessages(responseData.errors);
                    errorMessage = `${responseData.message || 'Validation failed'}:\n${errorMessages}`;
                }
                if (this.showToast) {
                    this.showToast("Error", errorMessage, "error");
                }
                return {
                    success: false,
                    message: errorMessage,
                    errors: responseData.errors,
                    statusCode: response.status
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to create notification');
        }
    }

    /**
     * Platform Admin Notifications - Get all
     * @param {Object} params - Pagination and filter parameters
     * @returns {Promise<Object>} - Returns paginated platform admin notifications
     */
    async getPlatformAdminNotifications(params = {}) {
        const API = `/platform-admin/notifications`;
        return this.getAllNotifications(API, params);
    }

    /**
     * Platform Admin Notifications - Get recent
     * @param {number} limit - Number of recent notifications to fetch (default 6)
     * @returns {Promise<Object>} - Returns recent platform admin notifications
     */
    async getRecentPlatformAdminNotifications(limit = 6) {
        const API = `/platform-admin/notifications/recent`;
        return this.getRecentNotifications(API, { limit });
    }

    /**
     * Platform Admin Notifications - Get statistics
     * @returns {Promise<Object>} - Returns platform admin notification statistics
     */
    async getPlatformAdminNotificationStatistics() {
        const API = `/platform-admin/notifications/statistics`;
        return this.getNotificationStatistics(API);
    }

    /**
     * Platform Admin Notifications - Mark as read
     * @param {number} notificationId - The notification ID
     * @returns {Promise<Object>} - Returns success status
     */
    async markPlatformAdminNotificationAsRead(notificationId) {
        const API = `/platform-admin/notifications/${notificationId}/read`;
        return this.markNotificationAsRead(API);
    }

    /**
     * Platform Admin Notifications - Mark all as read
     * @returns {Promise<Object>} - Returns success status
     */
    async markAllPlatformAdminNotificationsAsRead() {
        const API = `/platform-admin/notifications/mark-all-read`;
        return this.markAllNotificationsAsRead(API);
    }

    /**
     * Platform Admin Notifications - Delete
     * @param {number} notificationId - The notification ID
     * @returns {Promise<Object>} - Returns success status
     */
    async deletePlatformAdminNotification(notificationId) {
        const API = `/platform-admin/notifications/${notificationId}`;
        return this.deleteNotification(API);
    }

    /**
     * Platform Admin Notifications - Bulk delete
     * @param {Array} ids - Array of notification IDs to delete
     * @returns {Promise<Object>} - Returns success status
     */
    async bulkDeletePlatformAdminNotifications(ids) {
        const API = `/platform-admin/notifications/bulk-delete`;
        return this.bulkDeleteNotifications(API, { ids });
    }

    /**
     * Student Notifications - Get all for current student
     * @param {Object} params - Pagination and filter parameters
     * @returns {Promise<Object>} - Returns paginated student notifications
     */
    async getStudentNotifications(params = {}) {
        const studentId = getUserDetails("Id");
        if (studentId && !params.student_id) {
            params.student_id = studentId;
        }
        const API = `/student/notifications`;
        return this.getAllNotifications(API, params);
    }

    /**
     * Student Notifications - Get recent
     * @param {number} limit - Number of recent notifications to fetch (default 6)
     * @returns {Promise<Object>} - Returns recent student notifications
     */
    async getRecentStudentNotifications(limit = 6) {
        const studentId = getUserDetails("Id");
        const API = `/student/notifications/recent`;
        return this.getRecentNotifications(API, { student_id: studentId, limit });
    }

    /**
     * Student Notifications - Get statistics
     * @returns {Promise<Object>} - Returns student notification statistics
     */
    async getStudentNotificationStatistics() {
        const studentId = getUserDetails("Id");
        const API = `/student/notifications/statistics`;
        return this.getNotificationStatistics(API, { student_id: studentId });
    }

    /**
     * Student Notifications - Mark as read
     * @param {number} notificationId - The notification ID
     * @returns {Promise<Object>} - Returns success status
     */
    async markStudentNotificationAsRead(notificationId) {
        const API = `/student/notifications/${notificationId}/read`;
        return this.markNotificationAsRead(API);
    }

    /**
     * Student Notifications - Mark all as read
     * @returns {Promise<Object>} - Returns success status
     */
    async markAllStudentNotificationsAsRead() {
        const studentId = getUserDetails("Id");
        const API = `/student/notifications/mark-all-read`;
        return this.markAllNotificationsAsRead(API, { student_id: studentId });
    }

    /**
     * Student Notifications - Delete
     * @param {number} notificationId - The notification ID
     * @returns {Promise<Object>} - Returns success status
     */
    async deleteStudentNotification(notificationId) {
        const API = `/student/notifications/${notificationId}`;
        return this.deleteNotification(API);
    }

    /**
     * Student Notifications - Bulk delete
     * @param {Array} ids - Array of notification IDs to delete
     * @returns {Promise<Object>} - Returns success status
     */
    async bulkDeleteStudentNotifications(ids) {
        const API = `/student/notifications/bulk-delete`;
        return this.bulkDeleteNotifications(API, { ids });
    }

    /**
     * Supervisor Notifications - Get all for current supervisor
     * @param {Object} params - Pagination and filter parameters
     * @returns {Promise<Object>} - Returns paginated supervisor notifications
     */
    async getSupervisorNotifications(params = {}) {
        const supervisorId = getUserDetails("Id");
        if (supervisorId && !params.supervisor_id) {
            params.supervisor_id = supervisorId;
        }
        const API = `/supervisor/notifications`;
        return this.getAllNotifications(API, params);
    }

    /**
     * Supervisor Notifications - Get recent
     * @param {number} limit - Number of recent notifications to fetch (default 6)
     * @returns {Promise<Object>} - Returns recent supervisor notifications
     */
    async getRecentSupervisorNotifications(limit = 6) {
        const supervisorId = getUserDetails("Id");
        const API = `/supervisor/notifications/recent`;
        return this.getRecentNotifications(API, { supervisor_id: supervisorId, limit });
    }

    /**
     * Supervisor Notifications - Get statistics
     * @returns {Promise<Object>} - Returns supervisor notification statistics
     */
    async getSupervisorNotificationStatistics() {
        const supervisorId = getUserDetails("Id");
        const API = `/supervisor/notifications/statistics`;
        return this.getNotificationStatistics(API, { supervisor_id: supervisorId });
    }

    /**
     * Supervisor Notifications - Mark as read
     * @param {number} notificationId - The notification ID
     * @returns {Promise<Object>} - Returns success status
     */
    async markSupervisorNotificationAsRead(notificationId) {
        const API = `/supervisor/notifications/${notificationId}/read`;
        return this.markNotificationAsRead(API);
    }

    /**
     * Supervisor Notifications - Mark all as read
     * @returns {Promise<Object>} - Returns success status
     */
    async markAllSupervisorNotificationsAsRead() {
        const supervisorId = getUserDetails("Id");
        const API = `/supervisor/notifications/mark-all-read`;
        return this.markAllNotificationsAsRead(API, { supervisor_id: supervisorId });
    }

    /**
     * Supervisor Notifications - Delete
     * @param {number} notificationId - The notification ID
     * @returns {Promise<Object>} - Returns success status
     */
    async deleteSupervisorNotification(notificationId) {
        const API = `/supervisor/notifications/${notificationId}`;
        return this.deleteNotification(API);
    }

    /**
     * Supervisor Notifications - Bulk delete
     * @param {Array} ids - Array of notification IDs to delete
     * @returns {Promise<Object>} - Returns success status
     */
    async bulkDeleteSupervisorNotifications(ids) {
        const API = `/supervisor/notifications/bulk-delete`;
        return this.bulkDeleteNotifications(API, { ids });
    }

    /**
     * Institution Admin Notifications - Get all for current institution admin
     * @param {Object} params - Pagination and filter parameters
     * @returns {Promise<Object>} - Returns paginated institution admin notifications
     */
    async getInstitutionAdminNotifications(params = {}) {
        const institutionAdminId = getUserDetails("Id");
        if (institutionAdminId && !params.institution_admin_id) {
            params.institution_admin_id = institutionAdminId;
        }
        const API = `/institution-admin/notifications`;
        return this.getAllNotifications(API, params);
    }

    /**
     * Institution Admin Notifications - Get recent
     * @param {number} limit - Number of recent notifications to fetch (default 6)
     * @returns {Promise<Object>} - Returns recent institution admin notifications
     */
    async getRecentInstitutionAdminNotifications(limit = 6) {
        const institutionAdminId = getUserDetails("Id");
        const API = `/institution-admin/notifications/recent`;
        return this.getRecentNotifications(API, { institution_admin_id: institutionAdminId, limit });
    }

    /**
     * Institution Admin Notifications - Get statistics
     * @returns {Promise<Object>} - Returns institution admin notification statistics
     */
    async getInstitutionAdminNotificationStatistics() {
        const institutionAdminId = getUserDetails("Id");
        const API = `/institution-admin/notifications/statistics`;
        return this.getNotificationStatistics(API, { institution_admin_id: institutionAdminId });
    }

    /**
     * Institution Admin Notifications - Mark as read
     * @param {number} notificationId - The notification ID
     * @returns {Promise<Object>} - Returns success status
     */
    async markInstitutionAdminNotificationAsRead(notificationId) {
        const API = `/institution-admin/notifications/${notificationId}/read`;
        return this.markNotificationAsRead(API);
    }

    /**
     * Institution Admin Notifications - Mark all as read
     * @returns {Promise<Object>} - Returns success status
     */
    async markAllInstitutionAdminNotificationsAsRead() {
        const institutionAdminId = getUserDetails("Id");
        const API = `/institution-admin/notifications/mark-all-read`;
        return this.markAllNotificationsAsRead(API, { institution_admin_id: institutionAdminId });
    }

    /**
     * Institution Admin Notifications - Delete
     * @param {number} notificationId - The notification ID
     * @returns {Promise<Object>} - Returns success status
     */
    async deleteInstitutionAdminNotification(notificationId) {
        const API = `/institution-admin/notifications/${notificationId}`;
        return this.deleteNotification(API);
    }

    /**
     * Institution Admin Notifications - Bulk delete
     * @param {Array} ids - Array of notification IDs to delete
     * @returns {Promise<Object>} - Returns success status
     */
    async bulkDeleteInstitutionAdminNotifications(ids) {
        const API = `/institution-admin/notifications/bulk-delete`;
        return this.bulkDeleteNotifications(API, { ids });
    }


}


export const apiService = new ApiService();



